/**
 * Portfolio Hook - 获取用户资产数据
 *
 * 功能说明：
 * - 获取用户 USDC 余额（链上查询）
 * - 获取用户持仓价值（Data API）
 * - 获取用户未成交订单（CLOB API，需要认证）
 * - 计算总资产价值
 *
 * 数据来源：
 * - USDC 余额：通过 viem 链上查询 Safe 地址的 USDC 余额
 * - 持仓价值：通过 Data API 获取用户所有持仓的当前价值
 * - 未成交订单：通过 CLOB API 获取（需要认证的 clobClient）
 *
 * 使用方式：
 * ```tsx
 * const { safeAddress } = useSafeAddress();
 * const { clobClient } = useClobAuth();
 * const { usdcBalance, portfolioValue, totalValue, openOrders, isLoading } = usePortfolio(safeAddress, clobClient);
 * ```
 */
'use client';

import { useState, useEffect, useCallback } from 'react';
import { ClobClient, type OpenOrder } from '@polymarket/clob-client-v2';
import { predictApi, type UserPosition, type UserActivity } from '@/lib/predict-api';
import { getUserOpenOrders } from '@/lib/polymarket-clob';
import { StorageCache, CACHE_KEYS, CACHE_TTL } from '@/lib/storage-cache';
import { useTranslation } from 'react-i18next';
import { usePredictStore, usePredictCashBalance } from '@/app/stores/usePredictStore';

// ============ 常量配置 ============

/** 余额缓存数据类型(只缓 portfolioValue,USDC/pUSD 余额已搬到 store) */
interface BalanceCache {
  portfolioValue: number;
}

/** 余额缓存实例 */
const balanceCache = new StorageCache<BalanceCache>(CACHE_KEYS.BALANCE, CACHE_TTL.BALANCE, 'session');

// ============ Hook 返回类型 ============

export interface UsePortfolioReturn {
  /** USDC 现金余额 */
  usdcBalance: number;
  /** 持仓价值 */
  portfolioValue: number;
  /** 总资产 = 现金 + 持仓 */
  totalValue: number;
  /** 持仓列表 */
  positions: UserPosition[];
  /** 交易历史 */
  activity: UserActivity[];
  /** 未成交订单 */
  openOrders: OpenOrder[];
  /** 是否正在加载 */
  isLoading: boolean;
  /** 错误信息 */
  error: string | null;
  /** 刷新数据 */
  refresh: () => Promise<void>;
}

// ============ Hook 实现 ============

/**
 * Portfolio Hook
 *
 * @param safeAddress - 用户的 Polymarket Safe 地址
 * @param clobClient - 认证的 ClobClient 实例（可选，用于获取 open orders）
 * @returns {UsePortfolioReturn} 资产数据和状态
 *
 * @example
 * ```tsx
 * const { safeAddress } = useSafeAddress();
 * const { clobClient } = useClobAuth();
 * const { usdcBalance, totalValue, openOrders, isLoading } = usePortfolio(safeAddress, clobClient);
 *
 * if (isLoading) return <Spinner />;
 * return <div>Total: ${totalValue.toFixed(2)}</div>;
 * ```
 */
export function usePortfolio(safeAddress: string | null, clobClient?: ClobClient | null): UsePortfolioReturn {
  const { t } = useTranslation();
  // ============ 状态（从缓存初始化） ============

  // usdcBalance(展示用现金余额)从 store 派生,V1 → USDC.e / V2 → pUSD
  const usdcBalance = usePredictCashBalance();
  const refreshBalances = usePredictStore((s) => s.refreshBalances);

  const [portfolioValue, setPortfolioValue] = useState<number>(() => {
    const cached = balanceCache.get();
    return cached?.portfolioValue ?? 0;
  });
  const [positions, setPositions] = useState<UserPosition[]>([]);
  const [activity, setActivity] = useState<UserActivity[]>([]);
  const [openOrders, setOpenOrders] = useState<OpenOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ============ 刷新数据 ============

  /**
   * 刷新所有资产数据
   * 余额(USDC.e + pUSD)走 store.refreshBalances;持仓/活动/订单走 Data API + CLOB
   */
  const refresh = useCallback(async () => {
    // 无地址时清空数据
    if (!safeAddress) {
      setPortfolioValue(0);
      setPositions([]);
      setActivity([]);
      setOpenOrders([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('[usePortfolio] 获取数据，Safe 地址:', safeAddress);

      // 构建并行请求列表(余额刷新走 store action,与持仓数据并行)
      const requests: Promise<unknown>[] = [
        refreshBalances(safeAddress),
        predictApi.getUserValue(safeAddress),
        predictApi.getUserPositions({ user: safeAddress }).catch(() => []),
        predictApi.getUserActivity({ user: safeAddress }).catch(() => []),
      ];

      // 如果有认证的 clobClient，添加 Open Orders 请求
      if (clobClient) {
        requests.push(getUserOpenOrders(clobClient));
      } else {
        requests.push(Promise.resolve([]));
      }

      // 并行获取所有数据(refreshBalances 返回 void,占位)
      const [, value, userPositions, userActivity, userOrders] = (await Promise.all(requests)) as [
        void,
        number,
        UserPosition[],
        UserActivity[],
        OpenOrder[],
      ];

      console.log('[usePortfolio] 持仓价值:', value);
      console.log('[usePortfolio] 持仓数量:', userPositions.length);
      console.log('[usePortfolio] 交易记录:', userActivity.length);
      console.log('[usePortfolio] 未成交订单:', userOrders.length);

      setPortfolioValue(value);
      // 过滤掉 currentValue 为 0 的持仓（已结算归零的持仓）
      setPositions(userPositions.filter(p => p.currentValue !== 0));

      // 将 redeemable=true 且 currentValue=0 的持仓（待领取的亏损）转换为 activity 记录
      const redeemableLossPositions = userPositions
        .filter(p => p.redeemable && p.currentValue === 0)
        .map(
          p =>
            ({
              type: 'REDEEM',
              size: p.size,
              usdcSize: 0, // 亏损
              price: p.curPrice,
              side: '',
              title: p.title,
              outcome: p.outcome,
              slug: p.slug,
              eventSlug: p.eventSlug,
              icon: p.icon,
              // 使用秒级时间戳（与 activity 接口保持一致）
              timestamp: p.endDate ? Math.floor(new Date(p.endDate).getTime() / 1000).toString() : Math.floor(Date.now() / 1000).toString(),
              conditionId: p.conditionId,
              asset: p.asset,
            }) as UserActivity
        );

      // 合并 activity 和 redeemable loss positions，按时间倒序排序
      const mergedActivity = [...userActivity, ...redeemableLossPositions].sort((a, b) => {
        const timeA = Number(a.timestamp);
        const timeB = Number(b.timestamp);
        return timeB - timeA;
      });
      setActivity(mergedActivity);
      setOpenOrders(userOrders);

      // 缓存 portfolioValue,下次刷新快速显示(余额已在 store)
      balanceCache.set({ portfolioValue: value });
    } catch (err) {
      console.error('[usePortfolio] 获取数据失败:', err);
      setError(err instanceof Error ? err.message : t('predict.portfolio.error.getDataFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [safeAddress, clobClient, refreshBalances, t]);

  // ============ 自动刷新 ============

  /**
   * Safe 地址或 clobClient 变化时自动刷新数据
   */
  useEffect(() => {
    if (safeAddress) {
      refresh();
    }
  }, [safeAddress, clobClient, refresh]);

  // ============ 返回值 ============

  return {
    usdcBalance,
    portfolioValue,
    totalValue: usdcBalance + portfolioValue,
    positions,
    activity,
    openOrders,
    isLoading,
    error,
    refresh,
  };
}
